/**
 * How one Cost (WAC) cell got its number.
 *
 * The overview shows a single derived cost per size. When it looks wrong, the useful answer
 * is not a definition, it is the batches: what was paid, for how many units, which ones the
 * average leaves out and why. So this is a table, a division written out with the real
 * numbers in it, and a link to the full ledger for anything deeper.
 *
 * Nothing here recomputes the cost. `wac_cents` is the number the row showed, from the same
 * server function; `computed_wac_cents` is the batch average alone. They are equal except
 * when an override is in force, and that gap is the only thing on this screen that makes an
 * override visible rather than merely effective.
 *
 * The seven opening balance batches created at cutover carry no cost or a provisional one.
 * They are badged rather than hidden: a WAC of a few cents is not a bug on those SKUs, it is
 * an inheritance, and hiding the cause would leave the number looking like a defect.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { StockPageRow, StockWacBatch, StockWacBreakdown } from '@elutide/shared';
import {
  ChevronIcon,
  DefinitionPopover,
  Drawer,
  EmptyState,
  ErrorState,
  MoneyText,
  Pill,
  Skeleton,
} from '@/components';
import { ApiProblem } from '@/lib/api';
import { formatCount, formatMoney, shortDate, shortNyDate } from '@/lib/format';
import { useWacBreakdown } from './queries';
import { breakdownHasMoney } from './types';

export interface WacBreakdownDrawerProps {
  row: StockPageRow | null;
  onClose: () => void;
}

/** The six allocation buckets, in the order the batch table lists them. */
const ALLOCATIONS: readonly {
  key: keyof StockWacBatch['allocations'];
  label: string;
}[] = [
  { key: 'freight_cents', label: 'Freight' },
  { key: 'duties_cents', label: 'Duties' },
  { key: 'testing_cents', label: 'Testing' },
  { key: 'labeling_cents', label: 'Labeling' },
  { key: 'payment_fees_cents', label: 'Payment fees' },
  { key: 'other_cents', label: 'Other' },
];

/**
 * The six buckets as one figure, with the split behind a popover.
 *
 * Six columns of allocation would push the landed cost off the edge of the drawer and none of
 * them is read on its own. A `title` attribute would have been shorter and would have reached
 * a mouse only, so this uses the same `DefinitionPopover` the column headers use: it opens on
 * hover and on keyboard focus and is wired with `aria-describedby`. The trigger is a real
 * button because a tab stop that is not a control is its own accessibility problem.
 */
function AllocationsCell({ batch }: { batch: StockWacBatch }) {
  const parts = ALLOCATIONS.map(
    (entry) => `${entry.label} ${formatMoney(batch.allocations[entry.key], { showCents: true })}`,
  ).join(', ');
  return (
    <DefinitionPopover definition={parts} id={`alloc-${batch.batch_id}`} placement="above">
      <button type="button" className="stock-alloc">
        <MoneyText cents={batch.allocations.total_cents} showCents />
      </button>
    </DefinitionPopover>
  );
}

function statusTone(batch: StockWacBatch): 'neutral' | 'warn' | 'ok' {
  if (batch.is_opening_balance || batch.landed_unit_cents === 0) return 'warn';
  return batch.cost_status === 'final' ? 'ok' : 'neutral';
}

/**
 * A received batch carrying a landed cost of zero, that is not an opening balance.
 *
 * `landed_unit_cents` defaults to 0, so a receipt whose costs have not been entered yet drags
 * the average toward zero exactly like a cutover batch does, without the cutover's excuse. It
 * gets the same badge, because the operator's question is the same one.
 */
function isCostless(batch: StockWacBatch): boolean {
  return !batch.is_opening_balance && batch.landed_unit_cents === 0;
}

export function WacBreakdownDrawer({ row, onClose }: WacBreakdownDrawerProps) {
  const breakdown = useWacBreakdown(row ? row.product_id : null);
  const [excludedOpen, setExcludedOpen] = useState(false);

  // The drawer is mounted for the life of the page and only hidden, so without this the
  // excluded section stays open from whichever SKU was looked at last.
  const productId = row?.product_id ?? null;
  useEffect(() => setExcludedOpen(false), [productId]);

  if (!row) return null;

  const problem = breakdown.error instanceof ApiProblem ? breakdown.error : null;
  const title = `Cost of ${row.name}${row.size_label ? ` ${row.size_label}` : ''}`;

  return (
    <Drawer open onClose={onClose} title={title} id={`stock-wac-${row.product_id}`}>
      <p className="o-mono o-muted stock-drawer__sku">{row.sku ?? 'no SKU'}</p>

      {breakdown.isLoading ? (
        <Skeleton shape="card" lines={4} label="Loading cost breakdown" />
      ) : null}

      {/*
        Not `problem ? ...`: an error that is not an ApiProblem (a dropped connection, a body
        that did not parse) would otherwise leave all three branches false and render an empty
        dialog. Every failure gets a message and a retry.
      */}
      {breakdown.isError ? (
        <ErrorState
          message={problem ? problem.message : 'The cost breakdown did not load.'}
          requestId={problem ? problem.shortRequestId : null}
          onRetry={() => void breakdown.refetch()}
        />
      ) : null}

      {/* Opened from the Cost cell, which a packer never sees; if her response ever reached
          this drawer the breakdown has no cost in it and there is nothing to lay out. */}
      {breakdown.data && breakdownHasMoney(breakdown.data) ? (
        <Body
          data={breakdown.data}
          productId={row.product_id}
          excludedOpen={excludedOpen}
          onToggleExcluded={() => setExcludedOpen((open) => !open)}
        />
      ) : null}
    </Drawer>
  );
}

interface BodyProps {
  data: StockWacBreakdown;
  productId: number;
  excludedOpen: boolean;
  onToggleExcluded: () => void;
}

/** How the cost was arrived at, in the operator's words rather than the enum's. */
const SOURCE_LABEL: Readonly<Record<string, string>> = {
  wac: 'weighted average of received batches',
  opening: 'opening balances from the cutover',
  manual: 'a manual override',
  estimate: 'a supplier quote, not a receipt',
  none: 'nothing yet',
};

function Body({ data, productId, excludedOpen, onToggleExcluded }: BodyProps) {
  const overridden = data.override_cents !== null;
  const id = (part: string) => `stock-wac-${part}-${productId}`;
  const costless = data.batches.filter(isCostless).length;

  return (
    <>
      <section className="stock-drawer__section" aria-labelledby={id('headline')}>
        <h3 className="stock-drawer__heading" id={id('headline')}>
          Current cost
        </h3>
        <p className="stock-wac__headline">
          <MoneyText cents={data.wac_cents} showCents nullText="no cost" />
          <span className="o-muted"> per unit</span>
        </p>
        <p className="stock-wac__source">
          <Pill tone={data.wac_source === 'wac' ? 'ok' : 'warn'}>{data.wac_source}</Pill>{' '}
          <span className="o-muted">
            {SOURCE_LABEL[data.wac_source] ?? 'source not recognised'}
          </span>
        </p>
        <p className="o-muted stock-wac__formula">{data.formula}</p>

        {overridden ? (
          <p className="stock-wac__override">
            <Pill tone="warn">Manual override</Pill>{' '}
            {`Someone entered ${formatMoney(data.override_cents, { showCents: true })} by hand${
              data.override_at ? ` on ${shortDate(data.override_at)}` : ''
            }. The batch average below comes to ${formatMoney(data.computed_wac_cents, {
              showCents: true,
              nullText: 'no figure',
            })}, and the override wins.`}
          </p>
        ) : null}

        {costless > 0 ? (
          <p className="stock-wac__note">
            {`${costless} received batch${costless === 1 ? '' : 'es'} below carr${
              costless === 1 ? 'ies' : 'y'
            } a landed cost of zero without being an opening balance, which usually means the costs have not been entered yet. Until they are, this figure is too low.`}
          </p>
        ) : null}

        {data.opening_balance_count > 0 ? (
          <p className="stock-wac__note">
            {`${data.opening_balance_count} of these batches ${
              data.opening_balance_count === 1 ? 'is an' : 'are'
            } opening balance${data.opening_balance_count === 1 ? '' : 's'} created at cutover. They carry no cost or a provisional one, which is why this cost can look low.`}
          </p>
        ) : null}
      </section>

      <section className="stock-drawer__section" aria-labelledby={id('batches')}>
        <h3 className="stock-drawer__heading" id={id('batches')}>
          Batches in the average
        </h3>
        {data.batches.length === 0 ? (
          <EmptyState message="No received batch feeds this cost." />
        ) : (
          <div className="stock-scroll" data-allow-hscroll="true">
            <table className="stock-table">
              <caption className="o-visually-hidden">
                Batches feeding the weighted average cost
              </caption>
              <thead>
                <tr>
                  <th scope="col">Lot</th>
                  <th scope="col">Supplier</th>
                  <th scope="col">Received</th>
                  <th scope="col">Qty recd</th>
                  <th scope="col">Qty left</th>
                  <th scope="col">Goods</th>
                  <th scope="col">Allocations</th>
                  <th scope="col">Landed unit</th>
                  <th scope="col">Line total</th>
                  <th scope="col">Cost</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.batches.map((batch) => (
                  <tr key={batch.batch_id}>
                    <td className="o-mono">{batch.lot_number ?? 'no lot'}</td>
                    <td>{batch.supplier_name ?? 'no supplier'}</td>
                    <td>
                      {shortNyDate(batch.received_date) === 'unknown'
                        ? 'no date'
                        : shortNyDate(batch.received_date)}
                    </td>
                    <td className="stock-num o-num">{formatCount(batch.qty_received)}</td>
                    <td className="stock-num o-num">{formatCount(batch.qty_remaining)}</td>
                    <td className="stock-num">
                      <MoneyText cents={batch.unit_goods_cents} showCents />
                    </td>
                    <td className="stock-num">
                      <AllocationsCell batch={batch} />
                    </td>
                    <td className="stock-num">
                      <MoneyText cents={batch.landed_unit_cents} showCents />
                    </td>
                    <td className="stock-num">
                      <MoneyText cents={batch.line_landed_cents} showCents />
                    </td>
                    <td>{batch.cost_status}</td>
                    <td>
                      <Pill tone={statusTone(batch)}>
                        {batch.is_opening_balance
                          ? 'opening balance'
                          : isCostless(batch)
                            ? `${batch.status}, no cost`
                            : batch.status}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* The division, with the operands. A total the reader cannot check is a claim. */}
              <tfoot>
                <tr>
                  <th scope="row" colSpan={8}>
                    {`${formatMoney(data.total_landed_cents, { showCents: true })} landed over ${formatCount(data.total_qty_received)} units received`}
                  </th>
                  <td className="stock-num" colSpan={3}>
                    <span className="stock-wac__sum">
                      <MoneyText cents={data.computed_wac_cents} showCents nullText="no cost" />
                      <span className="o-muted"> per unit</span>
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {data.excluded.length > 0 ? (
        <section className="stock-drawer__section" aria-labelledby={id('excluded')}>
          <h3 className="stock-drawer__heading" id={id('excluded')}>
            Left out of the average
          </h3>
          <button
            type="button"
            className="stock-hidden__head"
            aria-expanded={excludedOpen}
            onClick={onToggleExcluded}
          >
            <ChevronIcon turn={excludedOpen ? 90 : 0} />
            <span>{`Excluded batches (${data.excluded.length})`}</span>
          </button>
          {excludedOpen ? (
            <div className="stock-scroll" data-allow-hscroll="true">
              <table className="stock-table">
                <caption className="o-visually-hidden">Batches excluded from the average</caption>
                <thead>
                  <tr>
                    <th scope="col">Lot</th>
                    <th scope="col">Supplier</th>
                    <th scope="col">Received</th>
                    <th scope="col">Qty</th>
                    <th scope="col">Landed unit</th>
                    <th scope="col">Status</th>
                    <th scope="col">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {data.excluded.map((batch) => (
                    <tr key={batch.batch_id}>
                      <td className="o-mono">{batch.lot_number ?? 'no lot'}</td>
                      <td>{batch.supplier_name ?? 'no supplier'}</td>
                      <td>
                        {shortNyDate(batch.received_date) === 'unknown'
                          ? 'no date'
                          : shortNyDate(batch.received_date)}
                      </td>
                      <td className="stock-num o-num">{formatCount(batch.qty_received)}</td>
                      <td className="stock-num">
                        <MoneyText cents={batch.landed_unit_cents} showCents />
                      </td>
                      <td>{batch.status}</td>
                      <td className="stock-wac__why">{batch.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <p className="stock-wac__more">
        <Link to="/stock/$productId/ledger" params={{ productId: String(productId) }}>
          Full batch and WAC ledger
        </Link>
      </p>
    </>
  );
}
